import React, { useState, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Layers, Database, Star, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import PageHeader from '../../../shared/ui/PageHeader';
import { DataSourceList } from '../components/modeler/DataSourceList';
import { AutoModelSummary } from '../components/modeler/AutoModelSummary';
import { StarSchemaPanel } from '../components/modeler/StarSchemaPanel';
import {
  getDatasources,
  getDataSourceModel,
  type DataSourceSummary,
} from '../services/modeler.service';
import { DataSourceModel, LocalStarModel } from '../../../types';
import toast from 'react-hot-toast';

type ViewMode = 'auto-model' | 'star-schema';

interface DataModelViewProps {
  datasourceId?: string | number;
  onCreateDashboard?: (datasourceIds: (string | number)[]) => void;
}

/**
 * Optimized DataModelView with instant loading and Star Schema preview
 */
const DataModelView: React.FC<DataModelViewProps> = ({ datasourceId, onCreateDashboard }) => {
  const [datasources, setDatasources] = useState<DataSourceSummary[]>([]);
  const [selectedDatasourceId, setSelectedDatasourceId] = useState<string | number | null>(null);
  const [model, setModel] = useState<DataSourceModel | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('star-schema');
  const [loading, setLoading] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache model to avoid re-fetching
  const [modelCache, setModelCache] = useState<Record<string | number, DataSourceModel>>({});

  // Load datasources on mount
  useEffect(() => {
    loadDatasources();
  }, []);

  // Load specific datasource if provided
  useEffect(() => {
    if (datasourceId) {
      setSelectedDatasourceId(datasourceId);
      loadModelForDatasource(datasourceId);
    }
  }, [datasourceId]);

  const loadDatasources = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDatasources();
      setDatasources(data);
      console.log(`✅ Loaded ${data.length} datasources`);
    } catch (err: any) {
      setError('Failed to load datasources');
      toast.error('Error loading datasources');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadModelForDatasource = async (id: string | number) => {
    // Check cache first
    if (modelCache[id]) {
      console.log(`✅ Using cached model for datasource ${id}`);
      setModel(modelCache[id]);
      return;
    }

    try {
      setLoadingModel(true);
      setError(null);
      
      console.log(`🔍 Fetching model for datasource ${id}...`);
      
      // Fetch complete model in one call (SmartSchema + AutoModel + StarSchema)
      const modelData = await getDataSourceModel(id);
      
      console.log('✅ Model loaded:', {
        hasSchema: !!modelData.schemaJson,
        hasAutoModel: !!modelData.autoModelJson,
        facts: modelData.starModelLocal.summary.totalFacts,
        dimensions: modelData.starModelLocal.summary.totalDimensions,
      });
      
      setModel(modelData);
      
      // Cache the model
      setModelCache(prev => ({ ...prev, [id]: modelData }));
      
      toast.success('Data model loaded');
      
    } catch (err: any) {
      setError('Failed to load data model. Has this datasource been analyzed?');
      toast.error('Error loading data model');
      console.error(err);
      setModel(null);
    } finally {
      setLoadingModel(false);
    }
  };

  const handleSelectDatasource = async (id: string | number) => {
    if (selectedDatasourceId === id) {
      // Deselect
      setSelectedDatasourceId(null);
      setModel(null);
    } else {
      // Select and load model
      setSelectedDatasourceId(id);
      await loadModelForDatasource(id);
    }
  };

  const handleRefreshModel = async () => {
    if (selectedDatasourceId) {
      // Clear cache for this datasource
      setModelCache(prev => {
        const newCache = { ...prev };
        delete newCache[selectedDatasourceId];
        return newCache;
      });
      
      // Reload
      await loadModelForDatasource(selectedDatasourceId);
    }
  };

  const selectedDatasource = datasources.find(ds => ds.id === selectedDatasourceId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <PageHeader
        title="Data Model Explorer"
        subtitle="View smart schemas, relationships, and star schema preview"
        icon={<Layers className="w-8 h-8" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar: Datasource List */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-xl p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-400" />
                  Datasources
                </h3>
                <button
                  onClick={loadDatasources}
                  disabled={loading}
                  className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {loading && datasources.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : (
                <DataSourceList
                  datasources={datasources}
                  selectedIds={selectedDatasourceId ? [selectedDatasourceId] : []}
                  onToggle={handleSelectDatasource}
                />
              )}
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {!selectedDatasourceId ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-xl p-12 text-center"
              >
                <Database className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Select a Datasource
                </h3>
                <p className="text-gray-400">
                  Choose a datasource from the list to explore its data model and star schema
                </p>
              </motion.div>
            ) : loadingModel ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-xl p-12"
              >
                <div className="flex flex-col items-center justify-center">
                  <Loader2 className="w-12 h-12 text-blue-400 animate-spin mb-4" />
                  <p className="text-white font-semibold">Loading data model...</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Fetching schema, auto-model, and generating star schema
                  </p>
                </div>
              </motion.div>
            ) : error && !model ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-500/10 border border-red-500/30 rounded-xl p-8"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Model</h3>
                    <p className="text-gray-300">{error}</p>
                    <button
                      onClick={handleRefreshModel}
                      className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : model ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Header with Datasource Info and View Mode Tabs */}
                <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {selectedDatasource?.name || 'Data Model'}
                      </h2>
                      {selectedDatasource?.description && (
                        <p className="text-gray-400 text-sm mt-1">
                          {selectedDatasource.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleRefreshModel}
                        disabled={loadingModel}
                        className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh model"
                      >
                        <RefreshCw className={`w-4 h-4 text-gray-400 ${loadingModel ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>

                  {/* View Mode Tabs */}
                  <div className="flex gap-2 mt-6">
                    <button
                      onClick={() => setViewMode('star-schema')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        viewMode === 'star-schema'
                          ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                          : 'bg-slate-700/30 text-gray-400 hover:bg-slate-700/50'
                      }`}
                    >
                      <Star className="w-4 h-4" />
                      Star Schema
                    </button>
                    {/* Auto Model deshabilitado - Ya tenemos Star Schema funcionando */}
                    <button
                      disabled
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-slate-700/30 text-gray-600 cursor-not-allowed opacity-50"
                      title="Funcionalidad deshabilitada - Usar Star Schema"
                    >
                      <Layers className="w-4 h-4" />
                      Auto Model (Próximamente)
                    </button>
                  </div>
                </div>

                {/* Content Area */}
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                    </div>
                  }
                >
                  {viewMode === 'star-schema' && (
                    <StarSchemaPanel
                      starModel={model.starModelLocal}
                      datasourceName={selectedDatasource?.name}
                    />
                  )}

                  {viewMode === 'auto-model' && model.autoModelJson && (
                    <AutoModelSummary autoModel={model.autoModelJson} />
                  )}

                  {viewMode === 'auto-model' && !model.autoModelJson && (
                    <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-xl p-12 text-center">
                      <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                      <p className="text-gray-400">
                        No auto-model available for this datasource
                      </p>
                    </div>
                  )}
                </Suspense>
              </motion.div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataModelView;
